import { useTranslation } from "react-i18next";
import styles from "./styles/PosTicketSearch.module.css";
import { useContext, useEffect, useRef, useState } from "react";

import Button from "../../../components/ui/button/Button";

import PosSearchGroup from "./PosSearchGroup";
import DataContext from "../../../context/DataContext";
import {
    removeWhiteSpace,
    stringTrim,
    toCamelCase,
} from "../../../utils/utils";

import POSDataContext from "../../../context/POSDataContext";

import PosScanTickets from "./PosScanTickets";

/**
 * The search form component
 * The composing of the filter expression is the most important
 * and complicated part in this component
 */
const PosSearchForm = () => {
    const {
        currentCustom, // {Object} - The object that contains the client customizations
        connection, // {Object} - The object that contains the client settings
        verifyReCaptcha, // {Function} - The function to verify reCAPTCHA
    } = useContext(DataContext);
    const {
        setFilterExpression, // {Function} - Function to set the filter expression
        filterExpression, // {String} - The current filter expression used for fetching tickets
        fetchTickets, // {Function} - Function to fetch tickets based on the filter expression
        handleErrorPos, // {Function} - Function to handle errors in POS module
        // verifyReCaptchaPos, // {Function} - The function to verify reCAPTCHA
    } = useContext(POSDataContext);

    const { t } = useTranslation(); // Using translation hook
    const [hoverFlag, setHoverFlag] = useState(false); // State to manage hover flag

    const [searchInput, setSearchInput] = useState({});
    const [ticketGroups, setTicketGroups] = useState([]);
    const [formButtonDisable, setFormButtonDisable] = useState(true);

    const [showScan, setShowScan] = useState(false);
    const [reset, setReset] = useState(false);
    // const [forceCheck, setForceCheck] = useState(false);

    // Format the ticket's search group and save it to a state.
    // Add a new key to each field for identifing it, the key name is starting
    // with "SIField-".
    // This key will be used later to extract the filter expression used to fitch tickets.
    // Add a new key to each field (detactionString), this key will be used
    // to validate the inputs (the relation between the input fields).
    // both keys are strings and has no value in the begening.
    // Search group object example:
    // {
    //     "id": 2,
    //     "fields": [
    //         {
    //             "id": 1,
    //             "fieldName": "Ticketno",
    //             "rel": "or",
    //             "label": "ticket number"
    //         },
    //         {
    //             "id": 2,
    //             "fieldName": "Plate",
    //             "rel": "or",
    //             "label": "licence plate"
    //         }
    //     ]
    // }
    useEffect(() => {
        const currentTicketGroups =
            currentCustom?.pos?.tickets &&
            currentCustom?.pos?.tickets.map((item, idx1) => ({
                ...item,
                fields: item.fields.map((field) => ({
                    ...field,
                    // the field identifier key is constructed from:
                    // Header prefix - "SIField-"
                    // Group index - idx1
                    // Field id - field?.id
                    // Field name in camel case - field.fieldName
                    [`SIField-${idx1}-${field?.id}-${toCamelCase(
                        field.fieldName,
                    )}`]: "",
                })),
                detectionString: "",
            }));
        setTicketGroups(currentTicketGroups);
    }, [currentCustom?.pos?.tickets]);

    // set the search inputs object that will be used for the form inputs
    // and set the default search group to the first one of the avialable groups on page mount
    useEffect(() => {
        setSearchInput(
            {
                groups: ticketGroups,
                searchGroup: ticketGroups?.[0]?.id,
            } || {},
        );
    }, [ticketGroups]);

    // validate the inputs of the active group
    // depending on the relation bbetween the fields.
    // If the relation is and and one of the fields is empty
    // the submitting will be disabled and the field will
    // turn red when hovering submit button.
    // If the relation is or, then submitting is allowed.
    // This will happen each time the searchGroup or the groups change
    // or when the hoverFlag state changes.
    useEffect(() => {
        // find the active group by the searchGroup

        if (searchInput?.groups && searchInput?.searchGroup) {
            const group = searchInput.groups.find(
                (group) => group.id === parseInt(searchInput.searchGroup),
            );
            // Validate the group inputs using the detectionString key.
            // The eval() function will take the string and evaluate it and return true or false.
            // The detection string is generated by another function.
            if (group) {
                const isDisabled = eval(group.detectionString);
                setFormButtonDisable(isDisabled); // set the form button disable state\
            }
        }
    }, [searchInput?.searchGroup, searchInput?.groups, hoverFlag]);

    const checkIfSubmitAllowed = (searchInput) => {
        if (searchInput?.groups && searchInput?.searchGroup) {
            const group = searchInput.groups.find(
                (group) => group.id === parseInt(searchInput.searchGroup),
            );
            if (group) {
                return !eval(group.detectionString); // return true if submit is allowed
            }
        }
        return false;
    };
    // Detect enter and ecape button
    const hoverTimerRef = useRef(null);
    const groupRef = useRef(searchInput?.searchGroup);

    useEffect(() => {
        if (hoverFlag) {
            clearTimeout(hoverTimerRef.current);
            setHoverFlag(false);
        }
    }, [searchInput?.searchGroup]);

    useEffect(() => {
        if (hoverFlag) {
            clearTimeout(hoverTimerRef.current);
            hoverTimerRef.current = setTimeout(() => {
                setHoverFlag(false);
            }, 2000);
        }
    }, [searchInput]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.code === "Enter") {
                const isAllowed = checkIfSubmitAllowed(searchInput);

                if (isAllowed) {
                    event.preventDefault();

                    submitData();
                } else {
                    setHoverFlag(true);
                    clearTimeout(hoverTimerRef.current);
                    hoverTimerRef.current = setTimeout(() => {
                        setHoverFlag(false);
                    }, 2000);
                }
            }

            if (event.code === "Escape") {
                event.preventDefault();
                resetFields();
            }
            if (event.ctrlKey && event.code === "Space") {
                event.preventDefault();
                setShowScan(true);
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
            clearTimeout(hoverTimerRef.current); // cleanup on unmount
        };
    }, [searchInput]);

    /**
     * Function to submit data, and trigger fetchTickets function
     * The altExpression provided in the function call,
     * it will be used to fetch the data.
     * If there is no altExpression, it will be generated
     * using getFilterExpression function
     * and then used to fetch the data
     */
    const submitData = async (altExpression) => {
        const expression = altExpression
            ? altExpression
            : getFilterExpression(searchInput); //generate expression
        const payLoad = {
            uniqueIdentifier: connection?.uniqueIdentifier,
            filterExpression: expression,
        };
        // save the expression to a state
        // this state uses session storage in the browser
        !filterExpression && setFilterExpression(payLoad);

        (await verifyReCaptcha()) && (await fetchTickets(payLoad));
    };

    /**
     * Generate filter expression for data fetching
     * example: "Ticketno == \"DZ660812\" && Plate == \"KNL8158\""
     * The steps are as follows:
     * 1- Find the active group.
     * 2- Find the last item index in the group fields.
     * 3- Loop throw the fields in the group and find the field identifier key (starts with "SIField").
     * 4- Take the identifier key value and add it to the filter expression string with the format in use.
     * 5- Add the relation between the fields to the filter expression string excluding the last field.
     * @param {Object} obj - The searchInput object containing user inputs data from the form
     * @returns {String} - The filter expression string
     */
    const getFilterExpression = (obj) => {
        const normalizeDateValue = (value) => {
            if (!value) return;
            // Check if it's a string in YYYY-MM-DD format
            if (
                typeof value === "string" &&
                /^\d{4}-\d{2}-\d{2}$/.test(value)
            ) {
                const [year, month, day] = value.split("-").map(Number);
                const date = new Date(Date.UTC(year, month - 1, day, 0, 0, 0)); // ← force UTC

                return date.toISOString(); // will give exactly 'YYYY-MM-DDT00:00:00.000Z'
            }

            // If not a valid date string, return as-is
            return value;
        };

        // Find the active group
        if (obj && obj?.groups && obj?.groups?.length) {
            const activeGroup = obj?.groups.filter(
                (group) => group?.id === parseInt(obj?.searchGroup),
            );

            let filterExpression = "";
            // Last field index
            const lastitemIndex = activeGroup[0]?.fields.length - 1;

            activeGroup[0]?.fields.map((field, idx) => {
                // Find the identifier key
                const key = Object.keys(field).find((key) =>
                    key.includes("SIField"),
                );
                const value = normalizeDateValue(field[key]); // identifier key value
                // Construct and format the string
                // Remove unneeded spaces using removeWhiteSpace function

                // filterExpression:"Ticketno == \"ewrth\" || Plate == \"wtry\""
                // "filterExpression": "SomeColumn.ToString().Replace(\" \", \"\").Contains(\"JoanneMarie\")"
                // "OFirstName.Contains(@0) && OLastName.Contains(@1)",
                //     "MICHAELJ",
                //     "ALOISIO";

                filterExpression +=
                    field?.type === "text-space"
                        ? `${field?.fieldName}.contains("${stringTrim(value)}")`
                        : `${field?.fieldName} == \"${removeWhiteSpace(
                              value,
                          )}\"`;

                // filterExpression += `${field?.fieldName} == \"${
                //     field?.type === "text"
                //         ? removeWhiteSpace(value)
                //         : stringTrim(value)
                // }\"`; // the old expression

                // filterExpression += `${
                //     field?.fieldName
                // }.ToString().Replace(" ", "").Contains("${removeWhiteSpace(
                //     value
                // )}")`;
                // Set the relation between the fields
                if (idx !== lastitemIndex) {
                    filterExpression += field?.rel === "and" ? ` && ` : ` || `;
                }
            });

            return filterExpression;
        }
    };

    /**
     * Submit handler
     */
    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!formButtonDisable) {
            submitData();
        }
    };

    /**
     * Change handler
     */
    const handleChange = (event) => {
        setSearchInput((prevsearchInput) => ({
            ...prevsearchInput,
            [event.target.name]:
                event.target.type === "checkbox"
                    ? event.target.checked
                    : event.target.value,
        }));
    };

    /**
     * Change handle for nested proporties
     */
    const handleNestedChange = (event, path) => {
        setSearchInput((prevInputs) => {
            const newState = { ...prevInputs };
            const keys = path.split(".");
            let obj = newState;

            while (keys.length > 1) {
                const key = keys.shift();
                obj = obj[key] ? obj[key] : {};
            }
            obj[keys[0]] = event.target.value;

            return newState;
        });
    };

    /**
     * Function to reset form fields
     * Set the field identifier key value to "" foreach field in all groups
     * Set the searchGroup to the first group id
     */
    const resetFields = () => {
        // Reset field identifier
        const updatedData = searchInput?.groups.map((item) => {
            const updatedFields = item.fields.map((field) => {
                Object.keys(field).forEach((key) => {
                    if (key.includes("SIField")) {
                        field[key] = "";
                    }
                });
                return field;
            });

            return { ...item, fields: updatedFields };
        });
        // set the search group id
        setSearchInput((prevInput) => ({
            ...prevInput,
            groups: updatedData,
            searchGroup: updatedData[0]?.id,
        }));

        setReset((prev) => !prev);
    };

    return (
        <>
            {showScan ? (
                <PosScanTickets
                    setShowScan={setShowScan}
                    submitData={submitData}
                    showScan={showScan}
                />
            ) : (
                <div
                    className={`${styles.main_form_container} v-flex-container`}
                >
                    <div
                        className={`${styles.form_header_container} flex-container`}
                    >
                        <h1 className={`${styles.form_header}`}>
                            {t(`search tickets`)}
                        </h1>
                    </div>
                    <form
                        className={`${styles.form_container} v-flex-container`}
                        onSubmit={handleSubmit}
                        noValidate={true} // Disable default HTML5 validation
                    >
                        <div className={`${styles.form_inputs_container} `}>
                            {/* Required validation, hovering the submit button triggers the hoverFlag and if the other conditions are met the filed will be highlited red */}

                            {searchInput?.groups &&
                                searchInput?.groups.length > 0 &&
                                searchInput?.groups.map((group, idx1) => (
                                    <PosSearchGroup
                                        key={`T-${group?.id}`}
                                        handleChange={handleChange}
                                        handleNestedChange={handleNestedChange}
                                        searchInput={searchInput}
                                        searchGroup={searchInput?.searchGroup}
                                        groupId={group?.id}
                                        groupIdx={idx1}
                                        fields={group?.fields}
                                        hoverFlag={hoverFlag}
                                        setSearchInput={setSearchInput}
                                        reset={reset}
                                    />
                                ))}
                        </div>
                        <div
                            className={`${styles.form_button_container} flex-container`}
                        >
                            <Button
                                body={t(`search`)}
                                type={"submit"}
                                disabled={formButtonDisable} // Disable button condition, the value is taken from formButtonDisable function
                                setHoverFlag={setHoverFlag}
                            />
                            <Button
                                body={t(`scan`)}
                                type={"button"}
                                onClickFuction={() => setShowScan(true)}
                            />
                            <Button
                                body={t(`clear`)}
                                type={"button"}
                                disabled={!searchInput?.groups?.length}
                                disableHTML={!searchInput?.groups?.length}
                                onClickFuction={resetFields}
                            />
                        </div>
                    </form>
                </div>
            )}
        </>
    );
};

export default PosSearchForm;
